import {Directive, HostBinding, HostListener, Input} from '@angular/core';
import {MatTooltip} from "@angular/material/tooltip";

/**
 * Directive: MultiLineTooltipDirective
 *
 * This directive simplifies the use of Angular Material tooltips (`matTooltip`) by:
 * - Automatically formatting tooltip text to support multiline display (replacing commas with line breaks).
 * - Applying default styling and positioning for consistent appearance.
 * - Allowing optional customization of tooltip class and position via inputs.
 *
 * ### Usage:
 * Apply the directive to any element that should display a formatted tooltip:
 *
 * ```html
 * <span [colimoMultiLineTooltip]="option.metaSummary"></span>
 * ```
 *
 * ### Optional Inputs:
 * - `tooltipClass`: CSS class applied to the tooltip. Default is `'multiline-tooltip-wide'`.
 * - `position`: Tooltip position. Accepts `'above' | 'below' | 'left' | 'right' | 'before' | 'after'`. Default is `'before'`.
 *
 * ### Notes:
 * This directive is especially useful for elements like `<mat-option>` that do not natively support tooltips.
 * It uses `MatTooltip` service and manual event listeners (`mouseover`, `mouseleave`) to control tooltip visibility.
 */
@Directive({
  selector: '[colimoMultiLineTooltip]',
  providers: [MatTooltip]
})
export class MultiLineTooltipDirective {

  /* directive used to default the position and tooltip class
  [matTooltip]="option.metaSummary ? option.metaSummary.replaceAll(',', ' \n ') : null" matTooltipClass="multiline-tooltip-wide" matTooltipPosition="before"
   */
  constructor(private tooltip: MatTooltip) {
  }

  @Input('colimoMultiLineTooltip') set metaSummary(value: string | null) {
    this.tooltipText = value ? value.replaceAll(',', ' \n ') : null;
  }
  @Input() tooltipClass = 'multiline-tooltip-wide';
  @Input() position: 'above' | 'below' | 'left' | 'right' | 'before' | 'after' = 'before';

  @HostBinding('attr.matTooltip') tooltipText: string | null = null;

  @HostListener('mouseover') mouseover() {
    if (!this.tooltipText) return;
    this.tooltip.message = this.tooltipText;
    this.tooltip.tooltipClass = this.tooltipClass;
    this.tooltip.position = this.position;
    this.tooltip.show();
  }
  @HostListener('mouseleave') mouseleave() {
    this.tooltip.hide();
  }
}
